import { useId, useRef, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { useTabDrafts } from "@/components/ui/box-draft";
import type { Result, RowTask } from "@/components/ui/box.schema";
import type { CourseConversation, InboxChannel } from "@/lib/api/inbox";
import { ApiError } from "@/lib/api/client";
import { reasonOnError } from "@/lib/api/commitSeams";
import { inboxWords as words } from "./inbox-copy";

export type PendingReply = { key: string; operation: string; legacy: boolean };
/** The UI consumes an observed answer, never a receipt's existence as a delivery claim. */
export type SendDecision =
  | { state: "confirmed"; at: string }
  | { state: "refused"; sentence: string }
  | { state: "unknown"; sentence?: string };
export interface ReplyTransport {
  send(conversation: CourseConversation, body: string, key: string): Promise<SendDecision>;
  check(conversation: CourseConversation, pending: PendingReply): Promise<SendDecision>;
}

/** Each row keeps its own draft and original command through Hide, a visit and reload. */
export function useConversationReplies(transport: ReplyTransport, previewByChannel: Partial<Record<InboxChannel,boolean>> = {}) {
  const fieldPrefix=useId();
  const draftOf = useTabDrafts<string, PendingReply>("conversation-reply");
  const [results, setResults] = useState<Record<string, Result>>({});
  const inFlight = useRef(new Map<string, Promise<Result>>());
  // Publishing pending through the tab store renders synchronously. A live send
  // stays Sending; only an unanswered send restored after it offers Check.
  const sending = useRef(new Set<string>());
  // A terminal Check is newer evidence than the still-cached row snapshot.
  const settledOperations = useRef(new Set<string>());
  const task = (row: CourseConversation): RowTask | undefined => {
    if (row.refresh_state === "unavailable") return undefined;
    const preview=Boolean(previewByChannel[row.channel]);
    // Replies append a new message. Incoming messages never rebase its text.
    const draft = draftOf({record:row.id,version:null,initial:""});
    const serverPending = row.operations.find(operation => !settledOperations.current.has(operation.id) && (operation.status === "pending" || operation.status === "unknown"));
    const pending = draft.pending ?? (serverPending ? {key:serverPending.id,operation:serverPending.id,legacy:true} : null);
    const id = `reply-${fieldPrefix}-${row.id}`;
    const settle = (result: Result) => {
      setResults(was => ({...was,[row.id]:result}));
      return result;
    };
    const answer = (decision: SendDecision, command: PendingReply): Result => {
      if (decision.state === "unknown") return unknown(command,decision.sentence);
      settledOperations.current.add(command.operation);
      draft.setPending(null);
      return decision.state === "confirmed"
        ? {state:"confirmed",at:decision.at,effects:[words.replySent],remains:[]}
        : {state:"refused",cap:words.replyNotSent,sentence:decision.sentence,draft:"kept"};
    };
    const unknown = (command: PendingReply, sentence: string = words.sendUnknown): Result => ({
      state:"unknown",cap:"",requestId:command.key,sentence,
      check:{verb:words.checkStatus,note:words.checkOnly,
        run:() => exclusive(async () => {
          try { return settle(answer(await transport.check(row,command),command)); }
          catch { return settle(unknown(command)); }
        }),
      },
    });
    function exclusive(run: () => Promise<Result>): Promise<Result> {
      const running = inFlight.current.get(row.id);
      if (running) return running;
      // Reserve before calling the transport, including synchronous re-entry.
      const promise = Promise.resolve().then(run).finally(() => inFlight.current.delete(row.id));
      inFlight.current.set(row.id,promise);
      return promise;
    }
    const result = results[row.id] ?? (pending && !sending.current.has(row.id) ? unknown(pending) : {state:"none"});
    return {
      parts:[{
        id,label:words.yourReply,owner:{system:"imam"},fold:"never",draft:"browser",keep:draft,
        state:{class:draft.kept ? "draft" : "quiet",word:draft.kept ? words.replyDraft : words.readyToReply},
        render:() => <Textarea id={id} aria-label={words.yourReply} value={draft.value}
          disabled={Boolean(pending)} maxLength={20000}
          onChange={event => {
            draft.set(event.target.value);
            if (result.state !== "unknown") setResults(was => ({...was,[row.id]:{state:"none"}}));
          }} />,
      }],
      result,
      commit:{verb:preview?words.sendPreview:words.sendReply,cap:words.whenYouSend,
        effects:() => [{id:`send-${row.id}`,sentence:words.sendToOtherPerson}],
        note:preview?words.privatePreview:words.privateToTwo,
        blockers:() => [
          ...(row.refresh_state === "reconnect_required" ? [{kind:"missing" as const,part:id,sentence:words.reconnectToReply}] : []),
          ...(!draft.value.trim() ? [{kind:"missing" as const,part:id,sentence:words.writeReply}] : []),
        ],
        run:key => exclusive(async () => {
          const unanswered = draftOf({record:row.id,version:null,initial:""}).pending ?? pending;
          if (unanswered) return settle(unknown(unanswered));
          const command: PendingReply = {key,operation:key,legacy:false};
          sending.current.add(row.id);
          draft.setPending(command);
          try { return settle(answer(await transport.send(row,draft.value.trim(),key),command)); }
          catch (error) {
            if (error instanceof ApiError && error.status >= 400 && error.status < 500 && error.status !== 408) {
              draft.setPending(null);
              return settle({state:"refused",cap:words.replyNotSent,sentence:reasonOnError(error)?.message ?? error.message,draft:"kept"});
            }
            return settle(unknown(command));
          } finally { sending.current.delete(row.id); }
        }),
      },
    };
  };
  return {task};
}
